import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useRequests } from "../../api/developmentRequests";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterBar } from "../../components/ui/FilterBar";
import { Page } from "../../components/ui/Page";
import { Pagination } from "../../components/ui/Pagination";
import { Spinner } from "../../components/ui/Spinner";
import { Table } from "../../components/ui/Table";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { usePaginationParams } from "../../hooks/usePaginationParams";
import { useSortParams } from "../../hooks/useSortParams";
import { useTranslation } from "../../hooks/useTranslation";
import { DevelopmentRequestResponse, statusLabel } from "../../types/developmentRequest";

const PAGE_SIZE = 20;

type StatusFilter = "all" | "draft" | "new" | "in_progress" | "completed";

export default function RequestsListPage() {
  const { t, language } = useTranslation();
  useLayoutTitle(t("requests.allRequests"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = (searchParams.get("status") ?? "all") as StatusFilter;
  const search = searchParams.get("search")?.trim() || undefined;
  const mine = searchParams.get("mine") === "true";
  const locked = searchParams.get("locked") === "1";
  const handleFilterChange = (value: StatusFilter) => {
    setSearchParams((prev) => {
      if (value === "all") prev.delete("status");
      else prev.set("status", value);
      prev.set("page", "1");
      return prev;
    });
  };

  type AudienceFilter = "" | "internal" | "external";
  const audience = (searchParams.get("audience") ?? "") as AudienceFilter;
  const handleAudienceChange = (value: AudienceFilter) => {
    setSearchParams((prev) => {
      if (!value) prev.delete("audience");
      else prev.set("audience", value);
      prev.set("page", "1");
      return prev;
    });
  };

  type SortKey = "title" | "createdAt";
  const { sortBy, sortDir, setSort } = useSortParams<SortKey>("createdAt");

  const rawPage = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));
  const { data, isLoading } = useRequests({
    status: filter === "all" ? undefined : filter,
    audience: audience || undefined,
    search,
    sortBy,
    sortDir,
    page: rawPage,
    limit: PAGE_SIZE,
    mine: mine || undefined,
  });

  const [requests, setRequests] = useState<DevelopmentRequestResponse[]>([]);
  useEffect(() => {
    data?.data && setRequests(data.data);
  }, [data?.data]);
  const { page, totalPages, setPage } = usePaginationParams(PAGE_SIZE, data?.total ?? 0);

  const filters: { value: StatusFilter; label: string }[] = [
    { value: "all", label: t("requests.filterAll") },
    { value: "draft", label: t("requests.statusDraft") },
    { value: "new", label: t("requests.statusNew") },
    { value: "in_progress", label: t("requests.statusInProgress") },
    { value: "completed", label: t("requests.statusCompleted") },
  ];

  const audienceFilters: { value: "internal" | "external"; label: string }[] = [
    { value: "internal", label: t("requests.audienceInternal") },
    { value: "external", label: t("requests.audienceExternal") },
  ];

  return (
    <Page>
      <Page.TitleRow>
        <Page.FilterRow>
          <FilterBar value={filter} options={filters} onChange={handleFilterChange} disabled={locked} />
          <Page.FilterDivider />
          <FilterBar<AudienceFilter>
            value={audience}
            options={audienceFilters}
            onChange={handleAudienceChange}
            nullable
          />
          {isLoading && <Spinner />}
        </Page.FilterRow>
        <Link to="/requests/new">
          <Button>+</Button>
        </Link>
      </Page.TitleRow>

      {requests.length === 0 ? (
        <EmptyState>{t("requests.noRequests")}</EmptyState>
      ) : (
        <>
          <Table>
            <Table.Head>
              <Table.HeaderCell sortDir={sortBy === "title" ? sortDir : null} onSort={() => setSort("title")}>
                {t("requests.title")}
              </Table.HeaderCell>
              <Table.HeaderCell>{t("requests.audience")}</Table.HeaderCell>
              <Table.HeaderCell>{t("requests.status")}</Table.HeaderCell>
              <Table.HeaderCell sortDir={sortBy === "createdAt" ? sortDir : null} onSort={() => setSort("createdAt")}>
                {t("requests.createdAt")}
              </Table.HeaderCell>
            </Table.Head>
            <Table.Body>
              {requests.map((req) => (
                <Table.Row key={req.id} onClick={() => navigate(`/requests/${req.id}`)}>
                  <Table.Cell variant="strong">{`${req.id}: ${req.title}`}</Table.Cell>
                  <Table.Cell>
                    <Badge
                      variant={req.audience}
                      label={
                        req.audience === "internal" ? t("requests.audienceInternal") : t("requests.audienceExternal")
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant={req.status} label={statusLabel(req.status, t)} />
                  </Table.Cell>
                  <Table.Cell variant="muted">{new Date(req.createdAt).toLocaleDateString(language)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </Page>
  );
}
